import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import { FaStar, FaCertificate, FaUsers, FaMedal, FaTrophy } from 'react-icons/fa';
import { fetchTeamRankings } from 'api';
import Loader from 'components/loaders/Loader';

// Define school colors dynamically
const schoolColors = {
  'SIMAD University': 'bg-green-500',
  'Tabsera Academy': 'bg-orange-500',
  'Itqan Academy': 'bg-red-500',
  'iForge Kenya': 'bg-gray-700',
  'iForge Somalia': 'bg-brand-500',
};

const AllTeams = () => {
  const [teams, setTeams] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        setLoading(true);
        console.log('AllTeams: Starting to load teams...');
        const fetchedTeams = await fetchTeamRankings();
        console.log('AllTeams: Teams loaded:', fetchedTeams);
        setTeams(fetchedTeams);
        
        // Add a minimum loading time to ensure users see the loading state
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (err) {
        console.error('AllTeams: Error loading teams:', err);
        setError('Error fetching teams from server.');
      } finally {
        console.log('AllTeams: Setting loading to false');
        setLoading(false);
      }
    };

    loadTeams();
  }, []);

  // Show loading state FIRST - this should be checked before any other conditions
  if (loading) {
    console.log('AllTeams: Showing loading state, loading:', loading);
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader text="Loading teams..." fullScreen={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-lg font-semibold text-red-500">
          {error}
        </div>
      </div>
    );
  }
  
  // Only check for empty teams AFTER loading is complete
  if (!teams || teams.length === 0) {
    return (
      <div className="flex flex-col items-center p-6 bg-white dark:bg-navy-800 rounded-lg shadow-lg dark:shadow-none">
        <FaMedal className="text-6xl text-orange-500 mb-4" />
        <h2 className="mb-2 text-2xl font-bold text-center text-gray-800 dark:text-white">
          No Teams Ranked Yet
        </h2>
        <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
          Be the first to create a team and start earning points to appear on the leaderboard!
        </p>
      </div>
    );
  }

  console.log('AllTeams: Rendering teams, loading:', loading, 'teams count:', teams.length);

  const rankedTeams = [...teams].sort((a, b) => b.points - a.points);
  const filteredTeams = rankedTeams.filter(team =>
    team.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const getRankBadge = (index) => {
    if (index === 0) return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
    if (index === 1) return 'bg-gradient-to-r from-gray-300 to-gray-500 text-white';
    if (index === 2) return 'bg-gradient-to-r from-orange-400 to-orange-600 text-white';
    return 'bg-gray-500 text-white';
  };

  const getRankIcon = (index) => {
    if (index === 0) return <FaTrophy className="text-yellow-400" />;
    if (index === 1) return <FaMedal className="text-gray-400" />;
    if (index === 2) return <FaMedal className="text-orange-400" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <header className="relative py-6 text-gray-800 dark:text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold">Leaderboard</h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-300">
            Explore teams, view achievements, and track progress.
          </p>
        </div>
        <div className="mt-4 border-b border-gray-300 dark:border-gray-700"></div>
      </header>

      {/* Search Bar */}
      <div className="container mx-auto px-4 py-4">
        <input
          type="text"
          placeholder="Search teams..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                     focus:outline-none focus:ring-2 focus:ring-orange-500 
                     bg-white dark:bg-navy-800 text-gray-800 dark:text-white"
        />
      </div>

      {/* Teams Grid Section */}
      <section className="container mx-auto px-4 py-6">
        {filteredTeams.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeams.map((team, index) => (
              <Card
                key={team.id}
                extra="p-6 transform transition-transform duration-300 hover:scale-105"
              >
                <div className="flex flex-col h-full">
                  {/* Rank Badge and Title */}
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getRankBadge(index)}`}>
                        #{index + 1}
                      </span>
                      {getRankIcon(index)}
                    </div>
                    <span
                      className={`px-3 py-1 text-white rounded-full text-xs font-semibold ${
                        schoolColors[team.schoolName] || 'bg-gray-400'
                      }`}
                    >
                      {team.schoolName}
                    </span>
                  </div>

                  {/* Team Name */}
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-3">
                    {team.name}
                  </h2>
                  
                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-300 flex-grow mb-4">
                    {team.description || "No description available for this team."}
                  </p>

                  {/* Stats */}
                  <div className="flex justify-between">
                    {/* Points */}
                    <div className="flex flex-col items-center">
                      <FaStar className="text-yellow-500 text-xl" />
                      <span className="mt-1 text-lg font-bold text-gray-800 dark:text-white">
                        {team.points}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Points</span>
                    </div>

                    {/* POAPs */}
                    <div className="flex flex-col items-center">
                      <FaCertificate className="text-orange-500 text-xl" />
                      <span className="mt-1 text-lg font-bold text-gray-800 dark:text-white">
                        {team.poaps} 
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">POAPs</span>
                    </div>

                    {/* Members */}
                    <div className="flex flex-col items-center">
                      <FaUsers className="text-brand-500 text-xl" />
                      <span className="mt-1 text-lg font-bold text-gray-800 dark:text-white">
                        {team.memberCount || team.members}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Members</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-600 dark:text-gray-300">
            No teams found.
          </div>
        )}
      </section>
    </div>
  );
};

export default AllTeams;
